import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getBusinessProfileBySlug, buildProfileSeo, listPublicBusinessProfiles, WEEK_DAYS, SOCIAL_NETWORKS, type CoreBusinessProfile, type BusinessHour } from '@/lib/coreBusinessProfile';
import { verificationLevelMeta } from '@/lib/coreVerification';
import ReviewsSection from '@/components/feature/ReviewsSection';
import { useBrand } from '@/hooks/useBrand';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 52. BUSINESS PROFILE (page publique)
// La page /business/:slug expose le profil public standardisé d'une
// entreprise, alimenté par le profil validé du socle. Un profil non public
// (is_public=false) n'est jamais affiché.
// ─────────────────────────────────────────────────────────────

interface DisplayItem {
  label: string;
  detail: string | null;
}

/** Normalise les listes de services / produits (chaînes ou objets). */
function normalizeItems(list: unknown[] | null): DisplayItem[] {
  if (!Array.isArray(list)) return [];
  return list
    .map((raw): DisplayItem => {
      if (typeof raw === 'string') return { label: raw, detail: null };
      if (raw && typeof raw === 'object') {
        const o = raw as Record<string, unknown>;
        const label = String(o.name ?? o.label ?? o.title ?? o.nom ?? '').trim();
        const detailRaw = o.description ?? o.desc ?? o.price ?? o.prix ?? null;
        return { label, detail: detailRaw != null ? String(detailRaw) : null };
      }
      return { label: String(raw), detail: null };
    })
    .filter((i) => i.label.length > 0);
}

function Stars({ rating }: { rating: number }) {
  const full = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5" title={`${rating.toFixed(1)} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <i
          key={n}
          className={n <= full ? 'ri-star-fill text-accent-500 text-sm' : 'ri-star-line text-foreground-300 text-sm'}
        ></i>
      ))}
    </span>
  );
}

export default function BusinessProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const { brand } = useBrand();
  const [profile, setProfile] = useState<CoreBusinessProfile | null>(null);
  const [related, setRelated] = useState<CoreBusinessProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    getBusinessProfileBySlug(slug)
      .then((data) => {
        if (!data) {
          setNotFound(true);
          return;
        }
        setProfile(data);
        setActivePhoto(0);
        // Autres entreprises publiques (mise en avant secondaire).
        listPublicBusinessProfiles({ category: data.category || undefined, limit: 6 })
          .then((list) => setRelated(list.filter((p) => p.id !== data.id).slice(0, 4)))
          .catch(() => setRelated([]));
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const seo = useMemo(() => (profile ? buildProfileSeo(profile) : null), [profile]);

  // SEO : titre, description, canonical + données structurées LocalBusiness.
  useEffect(() => {
    if (!seo || !profile) return;
    document.title = seo.title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', seo.description);
    setMeta('keywords', seo.keywords.join(', '));

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${window.location.origin}/business/${profile.slug || ''}`;

    const ldId = 'zifek-business-jsonld';
    const existing = document.getElementById(ldId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = ldId;
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: profile.name,
      description: seo.description,
      image: profile.logo || profile.cover || undefined,
      telephone: profile.phone || undefined,
      email: profile.email || undefined,
      url: profile.website || `${window.location.origin}/business/${profile.slug || ''}`,
      address: profile.address || profile.city
        ? { '@type': 'PostalAddress', streetAddress: profile.address || undefined, addressLocality: profile.city || undefined, addressCountry: profile.country || undefined }
        : undefined,
      aggregateRating: profile.rating
        ? { '@type': 'AggregateRating', ratingValue: profile.rating, reviewCount: profile.review_count || 0 }
        : undefined,
    });
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(ldId);
      if (s) s.remove();
    };
  }, [seo, profile]);

  const services = useMemo(() => normalizeItems(profile?.services ?? null), [profile]);
  const products = useMemo(() => normalizeItems(profile?.products ?? null), [profile]);

  const cover = profile?.cover || 'https://readdy.ai/api/search-image?query=Warm%20minimal%20artistic%20abstract%20gradient%20texture%20in%20soft%20terracotta%20and%20cream%20tones%2C%20elegant%20subtle%20grain%2C%20no%20text%2C%20editorial%20background%20for%20a%20professional%20business%20cover&width=1600&height=600&seq=business-cover-2026&orientation=landscape';
  const photos = (profile?.photos || []).filter(Boolean);
  const hours = (profile?.hours || {}) as Record<string, BusinessHour>;
  const hasHours = WEEK_DAYS.some((d) => hours[d.key]);
  const socials = (profile?.socials || {}) as Record<string, string>;
  const socialEntries = SOCIAL_NETWORKS.filter((s) => socials[s.key]);
  const whatsappDigits = (profile?.whatsapp || '').replace(/[^\d]/g, '');

  const mapQuery = profile?.latitude != null && profile?.longitude != null
    ? `${profile.latitude},${profile.longitude}`
    : [profile?.address, profile?.city, profile?.country].filter(Boolean).join(', ');
  const mapSrc = mapQuery ? `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed` : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  if (notFound || !profile || !seo) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-building-4-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">Entreprise introuvable</h1>
        <p className="text-sm text-foreground-500 mb-6">Ce profil n&apos;existe pas ou n&apos;est pas public.</p>
        <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline hover:bg-primary-600 transition-colors whitespace-nowrap">
          Retour à l&apos;accueil
        </Link>
      </div>
    );
  }

  const vMeta = verificationLevelMeta(profile.verification_level);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Barre supérieure minimale */}
      <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full px-4 md:px-6 h-14 md:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 no-underline">
            {brand.logo ? (
              <div className="w-8 h-8 rounded-lg overflow-hidden border border-background-200/50 flex items-center justify-center">
                <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain p-1" />
              </div>
            ) : (
              <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm font-heading">{brand.name.charAt(0).toUpperCase()}</span>
              </div>
            )}
            <span className="text-base font-bold font-heading text-foreground-950">{brand.name}</span>
          </Link>
          <Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors no-underline whitespace-nowrap">
            Accueil
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative h-[260px] md:h-[380px] overflow-hidden">
        <img src={cover} alt={`${profile.name} — couverture`} title={`${profile.name} ${profile.category || ''}`} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
      </section>

      <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
        {/* Carte d'identité */}
        <div className="-mt-16 md:-mt-20 relative z-10 bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-5">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden bg-background-100 border-4 border-background-50 flex-shrink-0 -mt-12 md:-mt-16">
              {profile.logo ? (
                <img src={profile.logo} alt={`${profile.name} — logo`} title={profile.name} className="w-full h-full object-contain bg-white p-2" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-foreground-400 font-heading">{profile.name.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-1">{profile.name}</h1>
              <div className="flex items-center gap-2 flex-wrap text-sm text-foreground-500">
                {profile.category && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium">
                    <i className="ri-price-tag-3-line"></i>{profile.category}
                  </span>
                )}
                {(profile.city || profile.country) && (
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-map-pin-line text-primary-500"></i>
                    {[profile.city, profile.country].filter(Boolean).join(', ')}
                  </span>
                )}
                {typeof profile.rating === 'number' && profile.rating > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <Stars rating={profile.rating} />
                    <span className="text-foreground-500">{profile.rating.toFixed(1)} ({profile.review_count || 0} avis)</span>
                  </span>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${vMeta.className}`}>
                  <i className={vMeta.icon}></i>
                  {vMeta.label}
                </span>
                {profile.languages && profile.languages.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-700">
                    <i className="ri-translate-2"></i>
                    {profile.languages.join(' · ')}
                  </span>
                )}
              </div>
            </div>

            {/* Actions de contact */}
            <div className="flex md:flex-col gap-2 flex-wrap md:w-44 flex-shrink-0">
              {profile.phone && (
                <a href={`tel:${profile.phone}`} className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600 transition-colors whitespace-nowrap">
                  <i className="ri-phone-line"></i>Appeler
                </a>
              )}
              {whatsappDigits && (
                <a href={`https://wa.me/${whatsappDigits}`} target="_blank" rel="noopener noreferrer" className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-green-500 text-white text-sm font-semibold no-underline hover:bg-green-600 transition-colors whitespace-nowrap">
                  <i className="ri-whatsapp-line"></i>WhatsApp
                </a>
              )}
            </div>
          </div>

          <p className="mt-4 text-xs text-foreground-400 flex items-start gap-1.5 leading-relaxed">
            <i className="ri-information-line mt-0.5"></i>
            Un niveau de vérification atteste uniquement que des critères définis par Zifek ont été satisfaits. Il ne constitue pas une garantie de qualité.
          </p>
        </div>

        {/* Corps */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 py-8">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">
            {(profile.description || profile.short_description) && (
              <section className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                <h2 className="text-lg font-bold font-heading text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-information-2-line text-primary-500"></i>À propos
                </h2>
                {profile.short_description && (
                  <p className="text-sm font-medium text-foreground-800 mb-2">{profile.short_description}</p>
                )}
                {profile.description && (
                  <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">{profile.description}</p>
                )}
              </section>
            )}

            {photos.length > 0 && (
              <section className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                <h2 className="text-lg font-bold font-heading text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-gallery-line text-primary-500"></i>Photos
                </h2>
                <div className="aspect-[16/9] rounded-lg overflow-hidden bg-background-100 mb-3">
                  <img src={photos[activePhoto]} alt={`${profile.name} ${activePhoto + 1}`} title={`${profile.name} ${profile.category || ''}`} className="w-full h-full object-cover" />
                </div>
                {photos.length > 1 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {photos.map((p, i) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setActivePhoto(i)}
                        className={`w-16 h-16 rounded-md overflow-hidden border-2 cursor-pointer transition-colors ${activePhoto === i ? 'border-primary-500' : 'border-background-200/70 hover:border-background-300/60'}`}
                      >
                        <img src={p} alt={`${profile.name} vignette ${i + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </section>
            )}

            {services.length > 0 && (
              <section className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                <h2 className="text-lg font-bold font-heading text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-service-line text-primary-500"></i>Services
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {services.map((s, i) => (
                    <div key={`${s.label}-${i}`} className="flex items-start gap-3 bg-background-100/70 border border-background-200/60 rounded-lg p-3">
                      <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-line"></i>
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground-900">{s.label}</p>
                        {s.detail && <p className="text-xs text-foreground-500 mt-0.5">{s.detail}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {products.length > 0 && (
              <section className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                <h2 className="text-lg font-bold font-heading text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-shopping-bag-3-line text-primary-500"></i>Produits
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {products.map((p, i) => (
                    <div key={`${p.label}-${i}`} className="flex items-start gap-3 bg-background-100/70 border border-background-200/60 rounded-lg p-3">
                      <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                        <i className="ri-price-tag-3-line"></i>
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground-900">{p.label}</p>
                        {p.detail && <p className="text-xs text-foreground-500 mt-0.5">{p.detail}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <ReviewsSection
              organizationId={profile.organization_id}
              entityType="business"
              title="Avis sur cette entreprise"
              emptyLabel="Aucun avis publié pour le moment. Soyez le premier à donner votre avis."
            />

            {mapSrc && (
              <section className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                <h2 className="text-lg font-bold font-heading text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-map-2-line text-primary-500"></i>Localisation
                </h2>
                <div className="rounded-lg overflow-hidden border border-background-200/70 h-[280px]">
                  <iframe title={`Carte de ${profile.name}`} src={mapSrc} className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
                </div>
              </section>
            )}
          </div>

          {/* Colonne latérale */}
          <aside className="space-y-6">
            <section className="bg-background-50 border border-background-200/70 rounded-lg p-5">
              <h2 className="text-sm font-bold font-heading text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-contacts-line text-primary-500"></i>Coordonnées
              </h2>
              <ul className="space-y-2.5 text-sm">
                {(profile.address || profile.city) && (
                  <li className="flex items-start gap-2.5 text-foreground-700">
                    <i className="ri-map-pin-line text-primary-500 mt-0.5"></i>
                    <span>{[profile.address, profile.city, profile.country].filter(Boolean).join(', ')}</span>
                  </li>
                )}
                {profile.phone && (
                  <li className="flex items-center gap-2.5">
                    <i className="ri-phone-line text-primary-500"></i>
                    <a href={`tel:${profile.phone}`} className="text-foreground-700 hover:text-primary-600 no-underline">{profile.phone}</a>
                  </li>
                )}
                {profile.email && (
                  <li className="flex items-center gap-2.5">
                    <i className="ri-mail-line text-primary-500"></i>
                    <a href={`mailto:${profile.email}`} className="text-foreground-700 hover:text-primary-600 no-underline break-all">{profile.email}</a>
                  </li>
                )}
                {profile.website && (
                  <li className="flex items-center gap-2.5">
                    <i className="ri-global-line text-primary-500"></i>
                    <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-foreground-700 hover:text-primary-600 no-underline break-all">{profile.website.replace(/^https?:\/\//, '')}</a>
                  </li>
                )}
              </ul>

              {socialEntries.length > 0 && (
                <div className="mt-4 pt-4 border-t border-background-200/70 flex items-center gap-2 flex-wrap">
                  {socialEntries.map((s) => (
                    <a key={s.key} href={socials[s.key]} target="_blank" rel="noopener noreferrer" title={s.label} className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors no-underline">
                      <i className={`${s.icon} text-lg`}></i>
                    </a>
                  ))}
                </div>
              )}
            </section>

            {hasHours && (
              <section className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <h2 className="text-sm font-bold font-heading text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-time-line text-primary-500"></i>Horaires
                </h2>
                <ul className="space-y-1.5 text-sm">
                  {WEEK_DAYS.map((d) => {
                    const h = hours[d.key];
                    if (!h) return null;
                    const closed = h.closed || (!h.open && !h.close);
                    return (
                      <li key={d.key} className="flex items-center justify-between">
                        <span className="text-foreground-500">{d.label}</span>
                        <span className={closed ? 'text-foreground-400' : 'text-foreground-800 font-medium'}>
                          {closed ? 'Fermé' : `${h.open || ''} – ${h.close || ''}`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {vMeta.key !== 'unverified' && (
              <section className="bg-accent-50 border border-accent-200 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-1.5">
                  <i className={`${vMeta.icon} text-accent-700`}></i>
                  <span className="text-sm font-bold text-accent-900">{vMeta.label}</span>
                </div>
                <p className="text-xs text-foreground-600 leading-relaxed">{vMeta.desc}</p>
              </section>
            )}
          </aside>
        </div>

        {/* Autres entreprises */}
        {related.length > 0 && (
          <section className="pb-10">
            <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-store-2-line text-primary-500"></i>Autres entreprises
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((r) => (
                <Link key={r.id} to={`/business/${r.slug || ''}`} className="bg-background-50 border border-background-200/70 rounded-lg p-4 no-underline hover:border-primary-300 transition-colors">
                  <div className="w-11 h-11 rounded-lg overflow-hidden bg-background-100 mb-3 flex items-center justify-center">
                    {r.logo ? (
                      <img src={r.logo} alt={r.name} className="w-full h-full object-contain bg-white p-1" />
                    ) : (
                      <span className="text-base font-bold text-foreground-400 font-heading">{r.name.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-foreground-900 truncate">{r.name}</p>
                  <p className="text-xs text-foreground-500 truncate mt-0.5">{[r.category, r.city].filter(Boolean).join(' · ') || '—'}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Pied de page */}
      <footer className="bg-secondary-100 border-t border-secondary-200 mt-4">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-600">
            {profile.name} — profil public alimenté par {brand.name}.
          </p>
          <Link to="/" className="text-xs text-primary-700 hover:text-primary-800 no-underline whitespace-nowrap">
            Découvrir {brand.name}
          </Link>
        </div>
      </footer>
    </div>
  );
}